import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type OpenPartnerPortalResult } from '@whinerp/client';
import { Button, PortalField, PortalShell, Skeleton } from '@whinerp/ui';
import { useT, useErrorMessage } from '../../app/i18n.js';
import { portalFieldLabel, portalShellLabels } from './portal-labels.js';
import { defineScreen } from '../types.js';

/**
 * SC-61 Partner portal (F-061).
 *
 * The same shell as every other public surface. What it lists is what the
 * server's partner allowlist returned, for the deals THIS person registered.
 *
 * The status it shows is the partner-facing one. "Under review" covers a
 * registration that collided with another partner's, deliberately: the screen
 * has no way to say otherwise because the response has no field that could.
 */

type Portal = OpenPartnerPortalResult;

const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

const BLANK = { companyName: '', domain: '', dealName: '', amount: '', currency: '', closeDate: '' };

function PartnerPortalScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const params = useParams();
  const token = params['token'] ?? '';
  const queryClient = useQueryClient();
  const [form, setForm] = useState(BLANK);
  const [registered, setRegistered] = useState(false);

  const page = useQuery<Portal, ApiError>({
    queryKey: ['portal', 'partner', token],
    queryFn: () => api.openPartnerPortal({ query: { token } }),
  });

  const register = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.registerPartnerDeal({
        body: { token, ...form, domain: form.domain.trim() === '' ? null : form.domain },
      }),
    onSuccess: async () => {
      setForm(BLANK);
      setRegistered(true);
      await queryClient.invalidateQueries({ queryKey: ['portal', 'partner', token] });
    },
  });

  if (page.isPending) return <PartnerLoading />;
  if (page.isError || !page.data) return <PartnerError />;

  const data = page.data;
  const set = (key: keyof typeof BLANK) => (event: { target: { value: string } }) => {
    setRegistered(false);
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  return (
    <PortalShell
      surface="partner"
      workspaceName={data.workspaceName}
      refusal={data.refusal}
      labels={portalShellLabels(t, data.workspaceName)}
    >
      <section className="flex flex-col gap-2">
        <h2 className="text-lead text-ink">{t('portal.yourDetails')}</h2>
        {data.contact.map((field) => (
          <PortalField key={field.key} data-portal-field={field.key} label={portalFieldLabel(t, field.key)} value={field.value} />
        ))}
      </section>

      <section className="flex flex-col gap-3" data-partner-register>
        <h2 className="text-lead text-ink">{t('partner.portal.register')}</h2>
        {register.error ? (
          <p data-partner-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {errorText(register.error)}
          </p>
        ) : null}
        {registered ? (
          <p data-partner-registered className="rounded-control bg-sunken px-3 py-2 text-dense text-ink">
            {t('partner.portal.registered')}
          </p>
        ) : null}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('partner.portal.company')}
            <input data-register-company className={INPUT} value={form.companyName} onChange={set('companyName')} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('partner.portal.website')}
            <input data-register-domain className={INPUT} value={form.domain} onChange={set('domain')} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('partner.portal.dealName')}
            <input data-register-deal className={INPUT} value={form.dealName} onChange={set('dealName')} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('partner.portal.closeDate')}
            <input data-register-close type="date" className={INPUT} value={form.closeDate} onChange={set('closeDate')} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('partner.portal.amount')}
            <input data-register-amount inputMode="decimal" className={INPUT} value={form.amount} onChange={set('amount')} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('partner.portal.currency')}
            <input data-register-currency maxLength={3} className={INPUT} value={form.currency} onChange={set('currency')} />
          </label>
        </div>
        <div>
          <Button intent="primary" size="small" data-register-submit disabled={register.isPending} onClick={() => register.mutate()}>
            {t('partner.portal.submit')}
          </Button>
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-lead text-ink">{t('partner.portal.deals')}</h2>
        {data.deals.length === 0 ? (
          <p data-partner-no-deals className="text-body text-ink-meta">
            {t('partner.portal.noDeals')}
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {data.deals.map((deal) => (
              <li
                key={deal.dealId}
                data-partner-deal={deal.dealId}
                data-partner-deal-status={deal.status}
                className="flex flex-col gap-2 rounded-card border border-rule p-3"
              >
                <span className="self-start rounded-chip bg-sunken px-2 py-0.5 text-meta text-ink-600">
                  {t(`partner.portal.status.${deal.status}` as 'partner.portal.status.accepted')}
                </span>
                <div className="flex flex-wrap gap-4">
                  {deal.fields.map((field) => (
                    <PortalField key={field.key} data-deal-field={field.key} label={portalFieldLabel(t, field.key)} value={field.value} />
                  ))}
                </div>
                {deal.note ? (
                  <p data-partner-deal-note className="text-body text-ink-meta">
                    {deal.note}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-lead text-ink">{t('partner.portal.collateral')}</h2>
        {data.collateral.length === 0 ? (
          <p data-partner-no-collateral className="text-body text-ink-meta">
            {t('partner.portal.noCollateral')}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {data.collateral.map((link) => (
              <li key={link.collateralId} data-partner-collateral={link.collateralId}>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-body text-bronze-text hover:underline">
                  {link.title}
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </PortalShell>
  );
}

function PartnerEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('partner.portal.noDeals')}</p>;
}

function PartnerLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex min-h-screen w-full flex-col items-center bg-canvas px-gutter py-6" role="status" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <div className="flex w-full max-w-reading flex-col gap-3">
        <Skeleton className="h-line-h2 w-panel-wide" />
        <Skeleton className="h-plot w-full" />
      </div>
    </div>
  );
}

function PartnerError(): JSX.Element {
  const t = useT();
  return (
    <PortalShell
      surface="partner"
      workspaceName={null}
      refusal={{ kind: 'invalid', canRequestNewLink: false }}
      labels={portalShellLabels(t, null)}
    >
      <span />
    </PortalShell>
  );
}

export const screen = defineScreen({
  id: 'SC-61',
  route: '/partner/:token',
  surface: 'page',
  chrome: 'bare',
  title: 'Partner portal',
  features: ['F-061'],
  entitlement: 'F-061',
  reachedBy: [{ kind: 'public' }],
  components: ['PortalShell'],
  elements: ['PortalShell', 'PortalField'],
  samplePortalToken: { surface: 'partner', param: 'token' },
  states: { empty: PartnerEmpty, loading: PartnerLoading, error: PartnerError },
  component: PartnerPortalScreen,
});

export default screen;
